import DOMPurify from 'dompurify';
import { useCallback, useEffect, useRef, useState } from 'react';
// Text-link-shaped toggle the styled lib/base Button cannot express;
// react-aria's Button keeps keyboard/focus/press semantics.
import { Button as AriaButton } from 'react-aria-components';

import {
  MEETING_SLOT_CLOSE_RE,
  MEETING_SLOT_OPEN_RE,
} from '@/lib/email/meetingSlotTag';

import { enforceLinkSafety } from './linkSafety';

// <meeting-slot id="UUID"/> tags come from the IdCodec when the AI uses
// calendar slot references in email drafts. DOMPurify strips unknown tags,
// leaving a silent hole in the sentence. Substitute BEFORE sanitizing so
// the reference survives as visible text. Self-closing is the canonical
// form; the paired variant is handled defensively.
const MEETING_SLOT_PLACEHOLDER =
  '<span style="font-weight:500">proposed time</span>';

function substituteMeetingSlotTags(html: string): string {
  MEETING_SLOT_OPEN_RE.lastIndex = 0;
  const opens: { start: number; end: number; selfClosing: boolean }[] = [];
  let m: RegExpExecArray | null;
  while ((m = MEETING_SLOT_OPEN_RE.exec(html)) !== null) {
    opens.push({
      start: m.index,
      end: m.index + m[0].length,
      selfClosing: m[0].endsWith('/>'),
    });
  }
  if (opens.length === 0) {
    return html;
  }

  let result = '';
  let cursor = 0;
  for (let i = 0; i < opens.length; i++) {
    const open = opens[i];
    result += html.slice(cursor, open.start) + MEETING_SLOT_PLACEHOLDER;
    let consumedTo = open.end;
    if (!open.selfClosing) {
      // A paired tag consumes through its matching close, dropping the inner
      // fallback text. Bound the close search to the region before the next
      // opening tag: these regions never overlap, so the total scan across all
      // openings is linear. An unclosed opening keeps only the placeholder.
      const nextOpenStart =
        i + 1 < opens.length ? opens[i + 1].start : html.length;
      const closeMatch = MEETING_SLOT_CLOSE_RE.exec(
        html.slice(open.end, nextOpenStart)
      );
      if (closeMatch !== null) {
        consumedTo = open.end + closeMatch.index + closeMatch[0].length;
      }
    }
    cursor = consumedTo;
  }
  result += html.slice(cursor);
  return result;
}

interface EmailIframeProps {
  html: string;
  className?: string;
  minHeight?: string;
  /**
   * Height clamp for the frame. Pass `null` to disable the clamp entirely:
   * the frame always grows to its content's natural height, so it never
   * scrolls internally and the surrounding container is the single scroll
   * context. Use this when the frame sits inside a scrollable region (e.g.
   * the email preview modal) — a clamped frame there creates a nested
   * scroll trap: wheel events over the email scroll the inner document
   * first, then chain to the outer container.
   */
  maxHeight?: string | null;
  /**
   * When true and the email's measured content exceeds `maxHeight`, a
   * "Show full message" toggle renders below the frame, releasing the
   * clamp. Without it, a long email is a scroll-trap: the reader scrolls
   * inside a fixed-height frame with no way to see the message at its
   * natural size. Default false — existing consumers keep the plain clamp.
   */
  expandable?: boolean;
}

function useIsDarkMode() {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

export function EmailIframe({
  html,
  className,
  minHeight = '100px',
  maxHeight = '400px',
  expandable = false,
}: EmailIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // The last document written to the iframe. Guards against back-to-back
  // identical srcdoc writes (e.g. an effect re-run with unchanged inputs):
  // each write starts a NEW iframe navigation, and rapid successive
  // navigations can race — the superseded document's timers keep posting
  // zero-layout messages through the REUSED contentWindow proxy while the
  // replacement navigation may never commit, leaving the frame blank.
  const lastWrittenDocRef = useRef<string | null>(null);
  const isDark = useIsDarkMode();
  const minHeightPx = parseInt(minHeight, 10) || 100;
  // `null` disables the clamp: Infinity makes clampedPx track the measured
  // content height and keeps isClamped permanently false (no toggle needed).
  const maxHeightPx =
    maxHeight === null
      ? Number.POSITIVE_INFINITY
      : parseInt(maxHeight, 10) || 400;
  // The iframe's true content height, as reported by its inline script.
  // Kept raw (unclamped) so the expandable path knows both what to clamp
  // to and whether there is anything hidden worth expanding.
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [isExpandedFull, setIsExpandedFull] = useState(false);

  // Listen for postMessage height updates from the sandboxed iframe
  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.source !== iframeRef.current?.contentWindow) {
      return;
    }
    if (event.data?.type === 'resize') {
      setContentHeight(event.data.height as number);
    }
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsExpandedFull(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    // Resolve CSS variables to actual color values before writing to iframe
    // (CSS variables don't cross iframe boundaries)
    const computedStyle = getComputedStyle(document.documentElement);

    const textColor = isDark
      ? computedStyle.getPropertyValue('--color-text-secondary').trim() ||
        '#9ca3af'
      : computedStyle.getPropertyValue('--color-text-secondary').trim() ||
        '#4b5563';

    const linkColor =
      computedStyle.getPropertyValue('--color-brand-600').trim() || '#7c3aed';

    const bgColor = isDark
      ? computedStyle.getPropertyValue('--color-bg-primary').trim() || '#111827'
      : computedStyle.getPropertyValue('--color-bg-primary').trim() ||
        '#ffffff';

    // Colors for quoted text styling
    const quotedBorderColor = isDark ? '#4b5563' : '#d1d5db';
    const quotedTextColor = isDark ? '#9ca3af' : '#6b7280';

    // A sanitization failure must not leave the frame showing a previous
    // document's content — bail before the write, showing nothing.
    // (Shape-only log: length, never content.)
    let sanitizedHtml = '';
    try {
      sanitizedHtml = enforceLinkSafety(
        DOMPurify.sanitize(substituteMeetingSlotTags(html))
      );
    } catch (error) {
      console.warn('[EmailIframe] sanitize pipeline threw', {
        htmlLen: html.length,
        error: error instanceof Error ? error.message : String(error),
      });
      // A failed update must not leave the previously rendered email on screen,
      // and must not leave a stale write key that would block a retry of this
      // exact input. Clear both before bailing. Also reset the measured height
      // and expansion state so the blank fail-closed frame does not retain the
      // previous message's height or keep offering its "Show full message"
      // control.
      lastWrittenDocRef.current = null;
      iframe.srcdoc = '';
      setContentHeight(null);
      setIsExpandedFull(false);
      return;
    }

    // Quote toggle logic runs inside the iframe as an inline script.
    // This avoids needing allow-same-origin (which combined with allow-scripts
    // would negate sandbox protection). Height updates are sent via postMessage.
    // THE WRITE GUARD: skip when this exact content (same html, same theme)
    // is already the iframe's document. Every srcdoc assignment starts a NEW
    // navigation, and back-to-back navigations race: the superseded
    // document's timers keep posting zero-layout messages through the reused
    // contentWindow proxy, and the replacing navigation can fail to commit —
    // leaving the frame blank. One write per distinct content, never two.
    const writeKey = `${isDark ? 'dark' : 'light'}|${minHeight}|${maxHeight}|${html}`;
    if (lastWrittenDocRef.current === writeKey) {
      return;
    }

    const inlineScript = `
(function() {
  function sendHeight() {
    var h = document.body ? document.body.scrollHeight : 0;
    parent.postMessage({ type: 'resize', height: h }, '*');
  }

  function setupQuoteToggles() {
    var allQuotes = document.querySelectorAll('.gmail_quote, blockquote');

    // Filter to only top-level quotes (not nested inside another quote)
    var topLevelQuotes = [];
    allQuotes.forEach(function(quote) {
      var parent = quote.parentElement;
      var isNested = false;
      while (parent && parent !== document.body) {
        if (parent.classList && (parent.classList.contains('gmail_quote') || parent.tagName === 'BLOCKQUOTE')) {
          isNested = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (!isNested) topLevelQuotes.push(quote);
    });

    var firstQuote = topLevelQuotes[0];
    if (!firstQuote) return;
    if (firstQuote.previousElementSibling && firstQuote.previousElementSibling.classList.contains('gmail_quote_toggle')) return;

    // Remove empty elements immediately before the quote (blank lines).
    // The quote might be wrapped in a container div, so check parent if no previous siblings.
    var targetElement = firstQuote;
    if (!firstQuote.previousSibling && firstQuote.parentNode && firstQuote.parentNode !== document.body) {
      targetElement = firstQuote.parentNode;
    }

    var prevSibling = targetElement.previousSibling;
    while (prevSibling) {
      var toCheck = prevSibling;
      prevSibling = prevSibling.previousSibling;

      if (toCheck.nodeType === 1) {
        var el = toCheck;
        if (el.tagName === 'BR') { el.remove(); continue; }
        if (['P', 'DIV', 'SPAN'].indexOf(el.tagName) !== -1) {
          if ((el.textContent || '').trim() === '' && !el.querySelector('img')) {
            el.remove(); continue;
          }
        }
        // Non-empty element found - clean trailing BRs inside it
        (function cleanTrailingBRs(element) {
          var last = element.lastChild;
          while (last) {
            if (last.nodeType === 1 && last.tagName === 'BR') {
              var toRemove = last; last = last.previousSibling; toRemove.remove(); continue;
            }
            if (last.nodeType === 3 && (last.textContent || '').trim() === '') {
              var toRemove = last; last = last.previousSibling;
              if (toRemove.parentNode) toRemove.parentNode.removeChild(toRemove); continue;
            }
            if (last.nodeType === 1 && last.lastChild) cleanTrailingBRs(last);
            break;
          }
        })(el);
        break;
      }
      // Remove whitespace-only text nodes
      if (toCheck.nodeType === 3 && (toCheck.textContent || '').trim() === '') {
        if (toCheck.parentNode) toCheck.parentNode.removeChild(toCheck);
        continue;
      }
      break;
    }

    // Mark for CSS targeting
    firstQuote.classList.add('top-level-quote');

    var toggle = document.createElement('button');
    toggle.className = 'gmail_quote_toggle';
    toggle.textContent = '\\u2026 Show quoted text';
    toggle.setAttribute('aria-label', 'Show quoted text');
    toggle.onclick = function() {
      var isExpanded = firstQuote.classList.contains('expanded');
      firstQuote.classList.toggle('expanded');
      toggle.textContent = isExpanded ? '\\u2026 Show quoted text' : '\\u25be Hide quoted text';
      toggle.setAttribute('aria-label', isExpanded ? 'Show quoted text' : 'Hide quoted text');
      // Send updated height after toggle animation
      setTimeout(sendHeight, 50);
    };
    if (firstQuote.parentNode) firstQuote.parentNode.insertBefore(toggle, firstQuote);
  }

  // Run setup after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setupQuoteToggles(); sendHeight(); });
  } else {
    setupQuoteToggles();
    sendHeight();
  }

  // Send height updates as images/fonts load
  setTimeout(sendHeight, 100);
  setTimeout(sendHeight, 500);
})();
`;

    const styledDocument = `<!DOCTYPE html>
<html>
<head>
<base target="_blank">
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  body {
    margin: 0;
    padding: 0;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: ${textColor};
    background-color: ${bgColor};
    word-break: break-word;
  }
  a {
    color: ${linkColor};
    text-decoration: underline;
  }
  img {
    max-width: 100%;
    height: auto;
  }
  p {
    margin: 0 0 8px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  /* Top-level quoted content - collapsed by default, marked by inline script */
  .top-level-quote {
    display: none;
  }
  .top-level-quote.expanded {
    display: block;
    margin-top: 8px;
    padding-left: 8px;
    border-left: 2px solid ${quotedBorderColor};
    color: ${quotedTextColor};
  }
  /* Nested quotes inside expanded content show normally */
  .expanded .gmail_quote,
  .expanded blockquote {
    display: block;
    margin-top: 8px;
    padding-left: 8px;
    border-left: 2px solid ${quotedBorderColor};
    color: ${quotedTextColor};
  }
  .gmail_quote_toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    margin: 4px 0 0 0;
    font-size: 12px;
    color: ${quotedTextColor};
    background: ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
    border: 1px solid ${quotedBorderColor};
    border-radius: 999px;
    cursor: pointer;
    font-family: system-ui, -apple-system, sans-serif;
    line-height: 1;
  }
  .gmail_quote_toggle:hover {
    background: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
    border-color: ${isDark ? '#6b7280' : '#9ca3af'};
  }
</style>
</head>
<body>${sanitizedHtml}<script>${inlineScript}</script></body>
</html>`;

    try {
      // Write HTML via srcdoc (no contentDocument access needed — avoids allow-same-origin)
      iframe.srcdoc = styledDocument;
      lastWrittenDocRef.current = writeKey;
    } catch (error) {
      console.warn('Failed to render email iframe:', error);
      // Component will show nothing on error
    }
  }, [html, isDark, minHeight, maxHeight]);

  const measured = contentHeight ?? minHeightPx;
  const clampedPx = Math.max(minHeightPx, Math.min(measured, maxHeightPx));
  const isClamped = measured > maxHeightPx;
  const displayHeight = isExpandedFull
    ? `${Math.max(measured, minHeightPx)}px`
    : `${clampedPx}px`;

  return (
    <>
      <iframe
        ref={iframeRef}
        className={className}
        style={{
          width: '100%',
          height: displayHeight,
          minHeight,
          maxHeight:
            isExpandedFull || maxHeight === null ? undefined : maxHeight,
          border: 'none',
          display: 'block',
          overflow: 'auto',
        }}
        sandbox='allow-scripts allow-popups allow-popups-to-escape-sandbox'
        title='Email content'
      />
      {expandable && isClamped && (
        <AriaButton
          onPress={handleToggleExpand}
          aria-expanded={isExpandedFull}
          className='mt-1 w-fit text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] underline underline-offset-2'
        >
          {isExpandedFull ? 'Collapse message' : 'Show full message'}
        </AriaButton>
      )}
    </>
  );
}
